/** Stage 27 — Branding overhaul
 *  Fetches global branding (public endpoint so the Login page works pre-auth) and
 *  applies primary/accent colors as HSL CSS variables + swaps favicon.
 *
 *  Returns: { companyName, logo, tagline, loginBg, primary, accent, ready }
 */
import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULTS = {
  companyName: "Aynoor Travels and Tours",
  logo: "",
  tagline: "Craft unforgettable journeys.",
  loginBg: "",
  primary: "#0F766E",
  accent: "#F59E0B",
};

// Convert #RRGGBB → "h s% l%" so it can be dropped into hsl(var(--primary)).
export const hexToHsl = (hex) => {
  if (!hex) return null;
  const m = hex.trim().replace("#", "");
  if (!/^([0-9a-f]{6}|[0-9a-f]{3})$/i.test(m)) return null;
  const full = m.length === 3 ? m.split("").map(c => c + c).join("") : m;
  const r = parseInt(full.slice(0, 2), 16) / 255;
  const g = parseInt(full.slice(2, 4), 16) / 255;
  const b = parseInt(full.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const applyBrandCss = (primaryHex, accentHex) => {
  const root = document.documentElement;
  const p = hexToHsl(primaryHex);
  const a = hexToHsl(accentHex);
  if (p) {
    root.style.setProperty("--primary", p);
    // Derive a slightly brighter glow variant (bump lightness by ~10%).
    const parts = p.split(" ");
    const l = Math.min(60, parseInt(parts[2]) + 10);
    root.style.setProperty("--primary-glow", `${parts[0]} ${parts[1]} ${l}%`);
  }
  if (a) root.style.setProperty("--gold", a);
};

const applyFavicon = (url) => {
  if (!url) return;
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = url;
};

export const useBranding = () => {
  const [state, setState] = useState({ ...DEFAULTS, ready: false });

  useEffect(() => {
    const load = () => axios.get(`${API}/settings/branding`).then(r => {
      const s = r.data || {};
      const next = {
        companyName: s.company_name || DEFAULTS.companyName,
        logo: s.company_logo || "",
        tagline: s.brand_tagline || DEFAULTS.tagline,
        loginBg: s.brand_login_bg_url || "",
        primary: s.brand_primary_color || DEFAULTS.primary,
        accent: s.brand_accent_color || DEFAULTS.accent,
        favicon: s.brand_favicon_url || "",
        ready: true,
      };
      applyBrandCss(next.primary, next.accent);
      applyFavicon(next.favicon);
      // Also stash the company name so the browser tab reflects it.
      if (next.companyName) document.title = `${next.companyName} — ERP`;
      setState(next);
    }).catch(() => setState({ ...DEFAULTS, ready: true }));

    load();
    // Stage 27 — Settings.jsx dispatches 'branding-updated' after a successful save so
    // every mounted useBranding() instance instantly reflects the new colors/logo/tagline.
    const handler = () => load();
    window.addEventListener("branding-updated", handler);
    return () => window.removeEventListener("branding-updated", handler);
  }, []);

  return state;
};

// Fire this after Settings save to broadcast a branding refresh across the app.
export const notifyBrandingUpdated = () => {
  window.dispatchEvent(new Event("branding-updated"));
};
